import React from 'react';
import ValidatorSummary, { IValidatorDecisionConfig } from 'components/ValidatorSummary';
import { AsyncTaskStatus } from 'constants/conceptLinkerValidatorStatus';
import { ITranslationProjectTranslationItem } from 'types/translationProject';

interface IProps {
  items: ITranslationProjectTranslationItem[];
  onFilterChange: (filter: { type: string; value: string } | null) => void;
  activeFilter: { type: string; value: string } | null;
  onClearFilter: () => void;
}

// Status constants matching AsyncTasksQueue
const STATUS_PENDING = 0;
const STATUS_DOING = 1;
const STATUS_DONE = 2;
const STATUS_FAILED = 3;

const TranslationProjectPreTranslateSummary: React.FC<IProps> = ({
  items,
  onFilterChange,
  activeFilter,
  onClearFilter,
}) => {
  // Map confidence levels to "decisions" for the ValidatorSummary
  // Updated ranges: Low (0-59), Medium (60-79), High (80-100)
  const decisions: IValidatorDecisionConfig[] = [
    { key: 'validated', label: 'Validated', color: '#4caf50' },
    { key: 'memory', label: 'Memory Match', color: '#2196f3' },
    { key: 'high', label: 'High Confidence', color: '#4caf50' },
    { key: 'medium', label: 'Medium Confidence', color: '#ff9800' },
    { key: 'low', label: 'Low Confidence', color: '#f44336' },
    { key: 'awaitingMemory', label: 'Awaiting Memory', color: '#78909C' },
  ];

  // Use normalized status for processing state
  const NORMALIZED_PROCESSING = 999;

  const statusConfig = {
    PENDING: NORMALIZED_PROCESSING,
    NEEDS_USER_REVIEW: -1, // Not used
    DONE: STATUS_DONE,
  };

  const asyncTaskStatusConfig = {
    FAILED: AsyncTaskStatus.FAILED,
    DEAD_LETTER: AsyncTaskStatus.DEAD_LETTER,
  };

  const handleFilterChange = (type: string, value: string) => {
    onFilterChange({ type, value });
  };

  // Check if there are any items currently being processed (doing)
  const hasDoing = items.some(item => item.async_task_status === STATUS_DOING);

  // Map items to validator format with confidence-based decisions
  // Use flatMap to allow items to appear in multiple categories
  const mappedItems = items.flatMap((item) => {
    // Normalize status - if no doing items, exclude pending from processing
    let normalizedStatus = item.async_task_status;
    if (item.async_task_status === STATUS_DOING) {
      normalizedStatus = NORMALIZED_PROCESSING;
    } else if (item.async_task_status === STATUS_PENDING && hasDoing) {
      normalizedStatus = NORMALIZED_PROCESSING;
    } else if (item.async_task_status === AsyncTaskStatus.AWAITING_MEMORY) {
      normalizedStatus = STATUS_DONE;
    }

    // Collect all applicable decisions for this item (CHECK ALL INDEPENDENTLY)
    const applicableDecisions: string[] = [];

    // Check if validated
    if (item.is_validated) {
      applicableDecisions.push('validated');
    }

    // Check if memory match (INDEPENDENT - no else)
    const similarity = item.item_info?.similarity;
    if (similarity !== null && similarity !== undefined && similarity >= 100) {
      applicableDecisions.push('memory');
    }

    // Check confidence level (INDEPENDENT - no else)
    if (item.confidence !== null && item.confidence !== undefined) {
      const confidencePercent = item.confidence > 1 ? item.confidence : item.confidence * 100;
      if (confidencePercent >= 80) {
        applicableDecisions.push('high');
      } else if (confidencePercent >= 60) {
        applicableDecisions.push('medium');
      } else {
        applicableDecisions.push('low');
      }
    }

    // Check if awaiting memory (deferred sibling)
    if (item.async_task_status === AsyncTaskStatus.AWAITING_MEMORY) {
      applicableDecisions.push('awaitingMemory');
    }

    // Handle items with no decisions
    if (applicableDecisions.length === 0) {
      // If item is processing but has no decisions yet, still return it so it counts as processing
      // Otherwise, exclude it entirely
      if (normalizedStatus === NORMALIZED_PROCESSING) {
        return [{
          ...item,
          status: normalizedStatus,
          decision: 'processing', // Use 'processing' as a placeholder decision
          ai_result: { decision: 'processing' },
        }];
      }
      return [];
    }

    // Create one item instance per applicable decision
    return applicableDecisions.map(decision => ({
      ...item,
      status: normalizedStatus,
      decision,
      ai_result: { decision },
    }));
  });

  return (
    <ValidatorSummary
      items={mappedItems}
      uniqueItemCount={items.length}
      decisions={decisions}
      statusConfig={statusConfig}
      asyncTaskStatus={asyncTaskStatusConfig}
      onFilterChange={handleFilterChange}
      activeFilter={activeFilter}
      onClearFilter={onClearFilter}
      enableChangeGrouping={false}
      customFeatures={{}}
      colors={{
        processing: '#9C27B0',
        review: '#FFA726',
        error: '#E53935',
      }}
    />
  );
};

export default TranslationProjectPreTranslateSummary;
